import { useState } from 'react';
import { useLocation } from '@tanstack/react-router';
import { Info, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useTranslation } from 'react-i18next';
import { useDemoMode } from '@/features/demo/useDemoMode';
import { useActiveSource } from '@/features/sources/useActiveSource';

const SESSION_KEY = 'demo-banner-dismissed';

/**
 * DemoBanner
 *
 * Sticky warning banner visible when useDemoMode().isDemoActive === true.
 * Tells the user they're viewing demo data and provides a CTA to /settings.
 * Dismissible per browser session via sessionStorage.
 *
 * Usage: render above <DataSourceBanner /> inside AppShell's main content column.
 */
export function DemoBanner() {
  const { t } = useTranslation();
  const { isDemoActive } = useDemoMode();
  const { source } = useActiveSource();
  const hasRecordSource = source?.role === 'record_source';
  const location = useLocation();
  const [dismissed, setDismissed] = useState(
    () => sessionStorage.getItem(SESSION_KEY) === '1'
  );

  // The dashboard renders DemoDataCard (same message, richer CTAs) — one demo
  // surface per page, so the strip yields there.
  if (location.pathname.startsWith('/dashboard')) return null;
  if (!isDemoActive || dismissed) return null;

  function handleDismiss() {
    sessionStorage.setItem(SESSION_KEY, '1');
    setDismissed(true);
  }

  return (
    <div
      data-testid="demo-banner"
      className={cn(
        'sticky top-0 z-[60] w-full',
        'bg-warning-dim border-b-2 border-warning',
        'px-4 py-2',
        'flex items-center justify-center gap-3'
      )}
    >
      <Info className="h-4 w-4 shrink-0 text-warning" />

      <div className="flex items-center gap-2">
        {/* Источник называется своим именем: у одного клиента это CRM, у
            другого телефония, у третьего — наш API. Пока подключено ничего
            нет, зовём подключить, а не рассказываем, что уже работает. */}
        <span className="text-sm text-fg">
          {hasRecordSource
            ? t('demo.banner.textConnected', {
                source: source.name,
                defaultValue:
                  'Демо-данные. {{source}} подключён — ваши записи появятся здесь после первых разговоров.',
              })
            : t(
                'demo.banner.text',
                'Демо-данные. Подключите источник коммуникаций, чтобы увидеть свои разговоры.'
              )}
        </span>
        {!hasRecordSource && (
          <a
            href="/settings?section=data_source"
            className={cn(
              'text-sm font-medium text-warning-fg underline underline-offset-2',
              'hover:opacity-80 transition-opacity'
            )}
          >
            {t('demo.banner.cta', 'Подключить')}
          </a>
        )}
      </div>

      <button
        onClick={handleDismiss}
        className={cn(
          'ml-2 rounded-button p-3 transition-colors',
          'text-warning-fg hover:bg-warning-dim',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-warning'
        )}
        aria-label={t('demo.banner.dismiss', 'Закрыть')}
        title={t('demo.banner.dismiss', 'Закрыть')}
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}
